import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  DefaultValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgForm,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'profet-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: DefaultValueAccessor,
      multi: true,
    },
  ],
})
export class InputComponent implements AfterViewInit, OnDestroy {
  // Subscription to unsubscribe all observables on component destroy
  onDestroyNotifier$ = new Subject<void>();

  @Input() label?: string | null = null;
  @Input() placeholder: string = '';
  @Input() type: 'text' | 'number' | 'numberString' | 'percentage' | 'tel' | 'email' | 'password' = 'text';
  @Input() prefixIcon: string | undefined;
  @Input() prefixText: string | undefined;
  @Input() suffixIcon: string | undefined;
  @Input() suffixText: string | undefined;
  @Input() infoText: string | undefined;
  @Input() hint: string | undefined;
  @Input() hideHint: boolean = false;
  @Input() hidden: boolean = false;
  @Input() required: boolean = false;
  @Input() allowNegativeNumbers: boolean = false;
  @Input() decimalPrecision: number = 0;
  @Input() minValue: number | null = null;
  @Input() maxValue: number | null = null;
  @Input() errorMessages: { [errorKey: string]: string } = {};

  @Output() suffixBtnClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostBinding('class.hidden') get hideClass() {
    return this.hidden;
  }

  @HostBinding('class.hint-hidden') get hintHideClass() {
    return this.hideHint;
  }

  constructor(
    public ngControl: NgControl,
    private _elementRef: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    this._applyRangeValidators();

    // Sync parent control value changes to update the display
    this.ngControl.control?.valueChanges
      .pipe(takeUntil(this.onDestroyNotifier$), distinctUntilChanged())
      .subscribe((value) => {
        // Trim empty strings to null
        if (value && typeof value === 'string' && value.trim() === '') {
          this.ngControl.control?.setValue(null, { emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  onSuffixBtnClick() {
    this.suffixBtnClicked.emit(true);
  }

  focus() {
    const input = this._elementRef.nativeElement.querySelector('input');
    input?.focus();
  }

  private _applyRangeValidators(): void {
    const ctrl = this.ngControl?.control;
    if (!ctrl) return;

    const validators = [];
    if (this.minValue != null) validators.push(Validators.min(this.minValue));
    if (this.maxValue != null) validators.push(Validators.max(this.maxValue));
    if (!validators.length) return;

    ctrl.addValidators(validators);
    ctrl.updateValueAndValidity({ emitEvent: false });

    if (this.minValue != null && this.errorMessages['min'] == null) {
      this.errorMessages = { ...this.errorMessages, min: `Minimum value is ${this.minValue}` };
    }
    if (this.maxValue != null && this.errorMessages['max'] == null) {
      this.errorMessages = { ...this.errorMessages, max: `Maximum value is ${this.maxValue}` };
    }
  }

  /**
   * Returns the keys of the errorMessages object for template iteration.
   */
  get errorMessageKeys(): string[] {
    return Object.keys(this.errorMessages);
  }

  get rangeHintText(): string | null {
    if (this.minValue != null && this.maxValue != null) return `Min: ${this.minValue}, Max: ${this.maxValue}`;
    if (this.minValue != null) return `Min: ${this.minValue}`;
    if (this.maxValue != null) return `Max: ${this.maxValue}`;
    return null;
  }

  get modelCtrl(): FormControl {
    return this.ngControl.control as FormControl;
  }

  get modelCtrlValue(): any {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (this.ngControl.control as FormControl)?.value;
  }

  set modelCtrlValue(value: any) {
    if (this.ngControl.control) {
      // If Number type, Explicitly cast the value to number
      if (this.type == 'number') {
        value = [null, undefined, ''].includes(value) ? null : Number(value);
        if (!isNaN(value)) {
          this.ngControl.control.setValue(value);
        }
      } else {
        if (typeof value == 'string') {
          this.ngControl.control.setValue(value.trim());
        } else {
          this.ngControl.control.setValue(value);
        }
      }
    }
  }

  /**
   * Custom error state matcher that checks the parent ngControl's state.
   * This ensures the error state respects form reset (which sets dirty to false).
   */
  get errorStateMatcher(): ErrorStateMatcher {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isErrorState: (_control: FormControl | null, _form: FormGroupDirective | NgForm | null): boolean => {
        const ctrl = this.ngControl?.control;
        return !!(ctrl && ctrl.invalid && ctrl.touched && ctrl.dirty);
      },
    };
  }
}
